import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import ContactCta from "@/components/site/ContactCta";
import { IMAGES } from "@/lib/site";
import { applySeo, ROUTE_SEO } from "@/lib/seo";

const SECTIONS = [
  {
    id: "the-wave",
    title: "The wave at Lagundri Bay",
    img: IMAGES.gallery1,
    alt: "The right-hand wave at Lagundri Bay seen from the Raffiel Nias deck",
    body: "Lagundri Bay at Sorake Beach is one of Indonesia's most famous right-hand reef breaks — a mechanical, barrelling wave that has drawn travelling surfers for decades. The main peak sits directly in front of Raffiel Nias, so you can check the sets from the rooftop café before you paddle out.",
  },
  {
    id: "when-to-go",
    title: "When to go",
    img: IMAGES.gallery2,
    alt: "Pool at Raffiel Nias at dusk overlooking the ocean",
    body: "Lagundri Bay receives its most reliable Indian Ocean swell through the middle of the year, with April to October the window most travelling surfers consider first. Shoulder months can suit surfers who prefer quieter line-ups — a season is a guide, not a forecast, so leave space in your plans if you can.",
  },
  {
    id: "getting-there",
    title: "Getting there — and getting your boards there",
    img: IMAGES.transferHero,
    alt: "Raffiel Nias transfer vehicle carrying surfboard bags",
    body: "Most guests fly via Medan (KNO) or Padang (PDG) into Binaka Airport (GNS), around 2.5 hours by car from Sorake Beach — we arrange direct airport transfers with room for board coffins. Because airlines charge heavily for surfboards on these routes, we also run fixed-rate board bag shipment overland from Medan and Padang, delivered to our reception.",
    link: { to: "/board-transfer", label: "See board transfer rates" },
  },
  {
    id: "stay-and-eat",
    title: "Where to stay and eat",
    img: IMAGES.dining,
    alt: "Beachside restaurant pavilion at Raffiel Nias at sunrise",
    body: "Raffiel Nias sits beachfront at Sorake with rooms from $45 a night, ocean view apartments for families and groups, and monthly stays from $700 for remote workers. On site you'll find a swimming pool, rooftop café with wave views, a beachside restaurant serving Indonesian and Western dishes, mini gym, games lounge and fast WiFi.",
    link: { to: "/rooms", label: "See rooms & rates" },
  },
];

export default function GuidePage() {
  useEffect(() => {
    applySeo(ROUTE_SEO["/guide"]);
  }, []);

  return (
    <div className="bg-sand-50 text-ink font-sans antialiased min-h-screen">
      <Navbar />
      <main>
        <section className="py-16 bg-sand-100 border-b border-sand-200" data-testid="guide-header">
          <div className="max-w-7xl mx-auto px-6">
            <span className="text-xs font-mono-clean text-terracotta uppercase tracking-wider font-semibold">
              Local Knowledge
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-ocean-900 mt-3">
              Sorake & Lagundri Bay surf guide
            </h1>
            <p className="text-sm text-fog mt-4 max-w-2xl leading-relaxed">
              Everything you need to plan a surf trip to South Nias — the wave,
              the seasons, how to get here from Medan or Padang, and where to
              stay and eat at Sorake Beach.
            </p>
          </div>
        </section>

        <section className="py-16" data-testid="guide-sections">
          <div className="max-w-7xl mx-auto px-6 space-y-20">
            {SECTIONS.map((s, i) => (
              <div
                key={s.id}
                data-testid={`guide-${s.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                  <img src={s.img} alt={s.alt} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div>
                  <span className="text-xs font-mono-clean text-ocean-700 uppercase tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-ocean-900 mt-2">
                    {s.title}
                  </h2>
                  <p className="text-sm text-fog leading-relaxed mt-4">{s.body}</p>
                  {s.link && (
                    <Link
                      to={s.link.to}
                      className="inline-block mt-5 text-sm font-semibold text-ocean-700 hover:underline"
                    >
                      {s.link.label} &rarr;
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
        <ContactCta />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
